import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  postadworker,
  getadvanceworker,
  deleteadworker,
  Updateadworker,
  ApproveAdvanceWorker,
} from "../../../Services/Services";

const AdvworkerTable = () => {
  const [Advworkers, setAdvworkers] = useState([]);
  const [filter, setFilter] = useState("");
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [AdvworkerToDelete, setAdvworkerToDelete] = useState(null);

  // ✅ NEW - Approve/Reject Modal States
  const [openApproveRejectModal, setOpenApproveRejectModal] = useState(false);
  const [workerToApprove, setWorkerToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null); // "Approved" | "Rejected"

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedWorker, setExpandedWorker] = useState(null);

  const userRole = localStorage.getItem("role");

  const [formData, setFormData] = useState({
    workername: "",
    designation: "",
  });

  useEffect(() => {
    fetchAdvworkers();
  }, []);

  const fetchAdvworkers = async () => {
    try {
      const response = await getadvanceworker();
      setAdvworkers(response);
    } catch (error) {
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      workername: "",
      designation: "",
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ UPDATED - Approve/Reject handler
  const handleApproveReject = async () => {
    if (!remarkInput.trim()) {
      toast.error("Remark is required!");
      return;
    }

    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveAdvanceWorker(
        workerToApprove.id,
        approvedById,
        actionType,
        remarkInput
      );

      setAdvworkers((prev) =>
        prev.map((item) => (item.id === workerToApprove.id ? updated : item))
      );

      const msg =
        actionType === "Approved"
          ? "Worker approved successfully!"
          : "Worker rejected successfully!";
      toast.success(msg);

      // ✅ Close modals and reset
      setOpenApproveRejectModal(false);
      setOpenEditModal(false);
      setRemarkInput("");
      setActionType(null);
      setWorkerToApprove(null);
    } catch (error) {
      toast.error("Failed to update approval status.");
    }
  };

  const handleAddNewAdvworker = async () => {
    const { workername, designation } = formData;

    if (!workername || !designation) {
      toast.error("All fields are required!");
      return;
    }

    try {
      await postadworker(formData);
      const updatedList = await getadvanceworker();
      setAdvworkers(updatedList);

      toast.success("Worker added successfully!");
      setOpenAddModal(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to add Worker");
    }
  };

  const handleUpdateAdvworker = async () => {
    try {
      await Updateadworker(editingItem.id, formData);
      const updatedList = await getadvanceworker();
      setAdvworkers(updatedList);

      toast.success("Worker updated successfully!");
      setOpenEditModal(false);
      setEditingItem(null);
      setWorkerToApprove(null);
      resetForm();
    } catch (error) {
      toast.error("Failed to update Worker");
    }
  };

  const handleEdit = (Advworker) => {
    setEditingItem(Advworker);
    setFormData({
      workername: Advworker.workername,
      designation: Advworker.designation,
    });
    setWorkerToApprove(null); // ✅ Clear approval state for normal edit
    setOpenEditModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteadworker(id);
      const updatedList = await getadvanceworker();
      setAdvworkers(updatedList);
      toast.info("Deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete Worker");
    }
  };

  const filteredAdvworkers = Advworkers.filter(
    (i) =>
      i.workername && i.workername.toLowerCase().includes(filter.toLowerCase())
  );

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  // ✅ Mobile Card Component
  const MobileWorkerCard = ({ worker, index }) => {
    const isExpanded = expandedWorker === worker.id;

    return (
      <Box key={worker.id}>
        <Box
          onClick={() =>
            setExpandedWorker(expandedWorker === worker.id ? null : worker.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: `4px solid ${worker.approval_status === "Approved"
              ? "#4caf50"
              : worker.approval_status === "Rejected"
                ? "#f44336"
                : "#ff9800"
              }`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                #{index + 1}
              </Typography>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                {worker.workername}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>{worker.designation}</span>
              <span style={{ color: "#999" }}>•</span>
              <Chip
                label={worker.approval_status || "Pending"}
                color={
                  worker.approval_status === "Approved"
                    ? "success"
                    : worker.approval_status === "Rejected"
                      ? "error"
                      : "warning"
                }
                size="small"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${worker.approval_status === "Approved"
                ? "#4caf50"
                : worker.approval_status === "Rejected"
                  ? "#f44336"
                  : "#ff9800"
                }`,
            }}
          >
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 1 }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  DESIGNATION
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {worker.designation}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  STATUS
                </Typography>
                <Chip
                  label={worker.approval_status || "Pending"}
                  color={
                    worker.approval_status === "Approved"
                      ? "success"
                      : worker.approval_status === "Rejected"
                        ? "error"
                        : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  APPROVED BY
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {worker.authorized_by_name || "—"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  DATE
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {formatOnlyDate(worker.created_at)}
                </Typography>
              </Box>
            </Box>

            {worker.remarks && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  REMARKS
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}>
                  {worker.remarks}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* ✅ Checkbox - Opens edit modal in approval mode */}
              {userRole != "Staff" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor:
                      worker.approval_status === "Approved"
                        ? "#e8f5e9"
                        : worker.approval_status === "Rejected"
                          ? "#ffebee"
                          : "#fff3e0",
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    border: `1px solid ${worker.approval_status === "Approved"
                      ? "#4caf50"
                      : worker.approval_status === "Rejected"
                        ? "#f44336"
                        : "#ff9800"
                      }`,
                  }}
                >
                  <Checkbox
                    checked={
                      worker.approval_status === "Approved" ||
                      worker.approval_status === "Rejected"
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      setEditingItem(worker);
                      setFormData({
                        workername: worker.workername,
                        designation: worker.designation,
                      });
                      setWorkerToApprove(worker);
                      setOpenEditModal(true);
                    }}
                    sx={{
                      padding: 0,
                      transform: "scale(0.75)",
                      color:
                        worker.approval_status === "Approved"
                          ? "#4caf50"
                          : worker.approval_status === "Rejected"
                            ? "#f44336"
                            : "#ff9800",
                      "&.Mui-checked": {
                        color:
                          worker.approval_status === "Approved"
                            ? "#4caf50"
                            : "#f44336",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color:
                        worker.approval_status === "Approved"
                          ? "#2e7d32"
                          : worker.approval_status === "Rejected"
                            ? "#c62828"
                            : "#f57c00",
                    }}
                  >
                    {worker.approval_status === "Approved"
                      ? "Approved"
                      : worker.approval_status === "Rejected"
                        ? "Rejected"
                        : "Approve"}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 0.6 }}>
                {/* Edit - Normal mode */}
                <IconButton
                  size="small"
                  disabled={worker.approval_status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(worker);
                  }}
                  sx={{
                    bgcolor:
                      worker.approval_status === "Approved" ? "#f5f5f5" : "#e8f5e9",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        worker.approval_status === "Approved" ? "#f5f5f5" : "#c8e6c9",
                      transform:
                        worker.approval_status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <EditIcon
                    sx={{
                      fontSize: "1rem",
                      color:
                        worker.approval_status === "Approved" ? "#bdbdbd" : "#4caf50",
                    }}
                  />
                </IconButton>

                {/* Delete */}
                <IconButton
                  size="small"
                  disabled={worker.approval_status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setAdvworkerToDelete(worker.id);
                  }}
                  sx={{
                    bgcolor:
                      worker.approval_status === "Approved" ? "#f5f5f5" : "#ffebee",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        worker.approval_status === "Approved" ? "#f5f5f5" : "#ffcdd2",
                      transform:
                        worker.approval_status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: "1rem",
                      color:
                        worker.approval_status === "Approved" ? "#bdbdbd" : "#f44336",
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1200, mx: "auto" }}>
      {/* Search + Add */}
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <TextField
          label="Search by Worker Name"
          variant="outlined"
          size="small"
          fullWidth
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{
            "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
            "& .MuiInputLabel-root": { fontSize: "0.7rem" },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            resetForm();
            setOpenAddModal(true);
          }}
          sx={{
            height: 30,
            borderRadius: 0,
            fontSize: "0.7rem",
            minWidth: 150,
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> ADV worker
        </Button>
      </Box>

      <Box
        sx={{
          maxHeight: isMobile ? "70vh" : "88vh",
          overflowY: "auto",
        }}
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
            {filteredAdvworkers.map((worker, index) => (
              <MobileWorkerCard key={worker.id} worker={worker} index={index} />
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "88vh",
              overflowX: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: "max-content",
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "0.75rem",
                      borderBottom: "1px solid #999",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {userRole != "Staff" && <TableCell>Approve</TableCell>}
                  <TableCell>S. No.</TableCell>
                  <TableCell>Worker Name</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Approval Status</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdvworkers.map((Advworker, index) => (
                  <TableRow
                    hover
                    key={Advworker.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "& td": {
                        padding: "0px",
                        fontSize: "0.7rem",
                      },
                      "&:hover": {
                        backgroundColor: "#e3f2fd !important",
                      },
                    }}
                  >
                    {/* ✅ Checkbox */}
                    {userRole != "Staff" && (
                      <TableCell>
                        <Checkbox
                          checked={
                            Advworker.approval_status === "Approved" ||
                            Advworker.approval_status === "Rejected"
                          }
                          onChange={() => {
                            setEditingItem(Advworker);
                            setFormData({
                              workername: Advworker.workername,
                              designation: Advworker.designation,
                            });
                            setWorkerToApprove(Advworker);
                            setOpenEditModal(true);
                          }}
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                            transform: "scale(0.6)",
                            color:
                              Advworker.approval_status === "Approved"
                                ? "green"
                                : Advworker.approval_status === "Rejected"
                                  ? "red"
                                  : "grey",
                            "&.Mui-checked": {
                              color:
                                Advworker.approval_status === "Approved"
                                  ? "green"
                                  : "red",
                            },
                          }}
                        />
                      </TableCell>
                    )}

                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{Advworker.workername}</TableCell>
                    <TableCell>{Advworker.designation}</TableCell>

                    <TableCell>
                      <Chip
                        label={Advworker.approval_status || "Pending"}
                        color={
                          Advworker.approval_status === "Approved"
                            ? "success"
                            : Advworker.approval_status === "Rejected"
                              ? "error"
                              : "warning"
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell>{Advworker.authorized_by_name || "—"}</TableCell>
                    <TableCell>{formatOnlyDate(Advworker.created_at)}</TableCell>
                    <TableCell>{Advworker.remarks || "—"}</TableCell>

                    <TableCell>
                      {/* ✅ Edit */}
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={() => handleEdit(Advworker)}
                        disabled={Advworker.approval_status === "Approved"}
                        color="primary"
                        size="small"
                      >
                        <EditIcon transform="scale(0.6)" />
                      </IconButton>

                      {/* Delete */}
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setAdvworkerToDelete(Advworker.id);
                        }}
                        disabled={Advworker.approval_status === "Approved"}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <DialogTitle>Add New Advance Worker</DialogTitle>
        <DialogContent>
          <TextField
            label="Worker Name"
            name="workername"
            value={formData.workername}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Designation"
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color="error">
            Close
          </Button>
          <Button onClick={handleAddNewAdvworker} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ UPDATED - Edit Modal with Conditional Footer */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setWorkerToApprove(null);
        }}
      >
        <DialogTitle>Edit Worker</DialogTitle>
        <DialogContent>
          <TextField
            label="Worker Name"
            name="workername"
            value={formData.workername}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Designation"
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          {/* ✅ Show Approve/Reject ONLY when checkbox was clicked */}
          {workerToApprove && userRole != "Staff" && (
            <>
              <Button
                color="success"
                size="small"
                onClick={() => {
                  setActionType("Approved");
                  setOpenApproveRejectModal(true);
                }}
              >
                Approve
              </Button>

              <Button
               sx={{color:'orange'}}
                size="small"
                onClick={() => {
                  setActionType("Rejected");
                  setOpenApproveRejectModal(true);
                }}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={handleUpdateAdvworker} color="primary">
            Edit
          </Button>
          <Button
            onClick={() => {
              setOpenEditModal(false);
              setWorkerToApprove(null);
            }}
            color="error"
          >
            Close
          </Button>


        </DialogActions>
      </Dialog>

      {/* ✅ NEW - Approve/Reject Modal */}
      <Dialog
        open={openApproveRejectModal}
        onClose={() => {
          setOpenApproveRejectModal(false);
          setRemarkInput("");
          setActionType(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {actionType === "Approved" ? "Approve Worker" : "Reject Worker"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter a remark before{" "}
            {actionType === "Approved" ? "approving" : "rejecting"} this worker:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={remarkInput}
            onChange={(e) => setRemarkInput(e.target.value)}
            placeholder="Enter remark..."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenApproveRejectModal(false);
              setRemarkInput("");
              setActionType(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color={actionType === "Approved" ? "success" : "error"}
            variant="contained"
            onClick={handleApproveReject}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setAdvworkerToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={() => {
                handleDelete(AdvworkerToDelete);
                setAnchorEl(null);
                setAdvworkerToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setAdvworkerToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default AdvworkerTable;